<script setup>
import { computed } from 'vue';
import {
  Menu, MenuButton, MenuItem, MenuItems,
} from '@headlessui/vue';
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/20/solid';

// The settings page's dropdown, for a list of options. An option draws as its label, or through the
// #option slot, and the chosen one through #selected.
const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  // { value, label }, plus anything the slots draw
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const selected = computed(() => props.options.find(({ value }) => value === props.modelValue));

function select(value) {
  if (value !== props.modelValue) emit('update:modelValue', value);
}
</script>

<template>
  <Menu as="div" class="relative inline-block w-full text-left">
    <MenuButton
      :disabled="disabled"
      :aria-label="label"
      class="relative inline-flex h-8 w-full items-center rounded-md border border-gray-700 bg-gray-800 px-2 py-2 text-sm font-medium text-text-primary shadow-sm hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:text-text-disabled disabled:hover:bg-gray-800"
    >
      <span class="flex flex-1 items-center gap-2 truncate text-left">
        <slot name="selected" :option="selected">{{ selected?.label }}</slot>
      </span>
      <ChevronDownIcon v-if="!disabled" class="-mr-1 ml-2 h-5 w-5 flex-shrink-0" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-50 mt-2 w-full min-w-[10rem] origin-top-right rounded-md border border-gray-700 bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        <div class="flex flex-col px-4 py-1">
          <MenuItem
            v-for="option in options"
            :key="option.value"
            v-slot="{ active }"
            @click="select(option.value)"
          >
            <div
              :class="[
                active ? 'text-text-primary' : 'text-text-secondary',
                'group flex w-full cursor-pointer items-center justify-start gap-2 py-2 text-sm',
              ]"
            >
              <slot name="option" :option="option">{{ option.label }}</slot>
              <CheckIcon
                v-if="option.value === modelValue"
                class="ml-auto h-5 w-5 text-primary-400"
                aria-hidden="true"
              />
            </div>
          </MenuItem>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>
